import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject, firstValueFrom, race } from 'rxjs';
import { filter, map, take, timeout } from 'rxjs/operators';
import { ConfigService } from 'app/core/services/config.service';
import { isPublicAuthRoute, isRunningInIframe, sanitizeAuthTarget } from './auth-cleanup';
import { TenantSsoResolver, TenantSsoInfo } from './tenant-sso-resolver';
import { SelfHostedAuthService } from './self-hosted-auth.service';

/**
 * Service to handle Auth0 initialization and token acquisition
 * This service ensures that authentication is complete before the app tries to use tokens
 */
@Injectable({
  providedIn: 'root'
})
export class AuthInitializerService {
  private initialized = false;
  private initializing = false;
  private static readonly TOKEN_EXPIRY_MARGIN_MS = 30000;
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private refreshInFlight: Promise<string | null> | null = null;
  private authReadySubject = new BehaviorSubject<boolean>(false);
  private redirectInProgressSubject = new BehaviorSubject<boolean>(false);

  // Proactive refresh: scheduled ahead of the current token's expiry so the
  // access token is renewed in the background while a tab/embed is open,
  // instead of only refreshing reactively after a request already failed.
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  public token$ = this.tokenSubject.asObservable();
  public authReady$ = this.authReadySubject.asObservable();
  public redirectInProgress$ = this.redirectInProgressSubject.asObservable();

  // Track if we're currently in the authentication redirect flow
  private isInAuthRedirect = false;

  // The most recent failure reason from forceTokenRefresh(), so waitForAuth()'s
  // final fallback error can carry useful diagnostic context instead of a
  // black-box "No token available" message. See #11370 / SLAPFIVE-FRONTEND-A28.
  private lastTokenRefreshError: unknown = null;

  constructor(
    private auth: AuthService,
    private router: Router,
    private configService: ConfigService,
    private ssoResolver: TenantSsoResolver,
    private selfHostedAuth: SelfHostedAuthService
  ) {
    // Check if we're in a redirect callback
    if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
      this.isInAuthRedirect = true;

      // Don't handle redirect callback if we're on the callback route - let AuthCallbackComponent handle it.
      //
      // Importantly, do NOT call this.initialize() here. initialize() calls
      // this.auth.isAuthenticated$ / getAccessTokenSilently() independently
      // of handleRedirectCallback(), which AuthCallbackComponent is about to
      // run. If the Auth0 SPA SDK already has a valid cached session/token
      // for this origin from an earlier visit (e.g. a support/admin user who
      // frequently switches between the same handful of client instances),
      // isAuthenticated$ resolves true against that STALE session and
      // getAccessTokenSilently() returns a token reflecting whatever role
      // was current the last time this origin was visited - NOT the
      // just-requested instance switch (which only takes effect once
      // handleRedirectCallback() processes the pending code/state and the
      // Auth0 Action re-runs with the target_subdomain hint). Racing that
      // against AuthCallbackComponent's correct, handleRedirectCallback()-
      // first flow can leave tokenSubject (and anything that queried GraphQL
      // in between) holding stale, wrong-tenant data even though the header
      // (DB-driven via /api/user) already shows the new client. See #12221.
      //
      // Just mark ourselves ready-to-wait so waitForAuth() callers block on
      // token$ until AuthCallbackComponent's setToken(freshToken) call.
      if (window.location.pathname === '/auth/callback') {
        this.isInAuthRedirect = false;
        // Clear the redirect in progress flag since we're now handling the callback
        sessionStorage.removeItem('auth_redirect_in_progress');
        this.redirectInProgressSubject.next(false);
        this.initialized = true;
        this.initializing = false;
        return;
      }

      try {
        // Clear any stored redirect state to prevent stale state issues
        sessionStorage.removeItem('auth_redirect_in_progress');

        // Handle the redirect callback without trying to parse the state yet
        // This prevents the "Invalid state" error
        this.auth.handleRedirectCallback().subscribe({
          next: (result) => {
            this.isInAuthRedirect = false;

            // Get a fresh token after successful redirect
            this.auth.getAccessTokenSilently().subscribe({
              next: (token) => {
                this.setToken(token);
                this.authReadySubject.next(true);
                this.initialized = true;
                this.initializing = false;

                // Navigate to the target URL if provided
                if (result?.appState?.target) {
                  sessionStorage.removeItem('auth_target_url'); // Clear stored URL
                  
                  // Check if we need to redirect to a different subdomain
                  if (result.appState.originalHost && result.appState.originalHost !== window.location.host) {
                    const redirectUrl = `${result.appState.originalProtocol}//${result.appState.originalHost}${result.appState.target}`;
                    window.location.href = redirectUrl;
                  } else {
                    void Promise.resolve(this.router.navigateByUrl(result.appState.target)).catch(() => {
                      void this.router.navigateByUrl('/client/home');
                    });
                  }
                } else {
                  // If no target is provided, navigate to the client home page
                  this.router.navigateByUrl('/client/home');
                }
              },
              error: (error) => {
                console.error('Error getting token after redirect:', error);
                this.router.navigateByUrl('/client/home');
                this.initialized = true;
                this.initializing = false;
              }
            });
          },
          error: (error) => {
            console.error('Error handling redirect callback:', error);
            this.isInAuthRedirect = false;

            // Even if there's an error with the state, we should try to initialize
            // This will at least attempt to get a token
            this.auth.isAuthenticated$.subscribe(isAuthenticated => {
              if (isAuthenticated) {
                this.auth.getAccessTokenSilently().subscribe({
                  next: (token) => {
                    this.setToken(token);
                    this.authReadySubject.next(true);
                    this.initialized = true;
                    this.initializing = false;
                    sessionStorage.removeItem('auth_target_url'); // Clear stored URL
                    this.router.navigateByUrl('/client/home');
                  },
                  error: (err) => {
                    console.error('Failed to get token after redirect error:', err);
                    this.initialized = true;
                    this.initializing = false;
                    this.router.navigateByUrl('/client/home');
                  }
                });
              } else {
                this.initialized = true;
                this.initializing = false;
                this.router.navigateByUrl('/client/home');
              }
            });
          }
        });
      } catch (error) {
        console.error('Exception during redirect callback handling:', error);
        this.isInAuthRedirect = false;
        this.initialized = true;
        this.initializing = false;
        this.router.navigateByUrl('/client/home');
      }
    } else {
      // If we are not in a redirect, we should still initialize the service.
      // The AuthGuard will handle any necessary login redirects for protected routes.
      this.initialize();
    }
  }

  /**
   * Initialize the authentication state
   */
  async initialize(): Promise<void> {
    if (this.initialized || this.initializing) {
      return;
    }

    this.initializing = true;

    try {
      // Self-hosted sessions (#16114): a stored SlapFive JWT is authoritative
      // and survives page reloads. Without this check, Auth0 correctly reports
      // isAuthenticated=false on every refresh (there is no Auth0 session) and
      // the route guard below bounces the user back to /auth/login, discarding
      // the valid stored session. See the same precedence rule in getToken().
      // If nothing is stored, try the shared-domain s5_session cookie so a
      // session from a sibling tenant subdomain resumes without a password
      // prompt (instance switching).
      const selfHostedToken = this.selfHostedAuth.getToken()
        || await this.selfHostedAuth.restoreSession();
      if (selfHostedToken) {
        this.setToken(selfHostedToken);
        return;
      }

      const isAuthenticated = await firstValueFrom(
        this.auth.isAuthenticated$.pipe(filter(auth => auth !== undefined), take(1))
      );

      // Check if we have a login_hint in the URL that implies we need to switch context
      // This is critical for the production instance switch flow
      const params = new URLSearchParams(window.location.search);
      const loginHint = params.get('login_hint');
      
      if (loginHint && loginHint.startsWith('subdomain:')) {
          await this.login();
          return;
      }

      if (isAuthenticated) {
        try {
          const token = await firstValueFrom(this.auth.getAccessTokenSilently());
          this.setToken(token);
        } catch (error) {
          console.error('Failed to get initial token:', error);
          // If we fail to get a token for ANY reason (Timeout, 403, Missing Refresh Token),
          // we must treat the session as invalid — but only on routes that
          // actually require auth. Public routes (mobile prompt response,
          // open surveys, story viewers) must keep working for anonymous
          // members; sending them to login is #13276.
          if (this.isRouteRequiringAuth()) {
            console.log('Token fetch failed, forcing re-authentication via explicit login.');
            await this.login();
          }
          return;
        }
      } else {
        // If not authenticated and not in a redirect, check if we should redirect
        // This handles the case where we just came from a successful callback
        // but the AuthInitializerService wasn't involved in the redirect handling
        if (this.isRouteRequiringAuth()) {
          await this.login();
        }
      }
    } catch (error) {
      console.error('Error initializing Auth state:', error);
    } finally {
      this.initialized = true;
      this.initializing = false;
      this.authReadySubject.next(true);
    }
  }

  /**
   * Initiate a fresh login flow with proper state setup. Resolves the tenant's
   * SSO connection before redirecting so SSO-only tenants never land on Auth0's
   * password-capable Universal Login page.
   */
  /**
   * Build the Auth0 redirect configuration for the current URL, including the
   * tenant SSO connection when one exists. Exposed for unit testing so the
   * iframe-specific redirect side-effect can be bypassed.
   */
  async buildLoginConfig(): Promise<{ appState: any; authorizationParams: any; selfHosted?: boolean }> {
    // Sanitized so an auth-flow-only route (e.g. /logout, which would
    // immediately re-log the user out on mount) can never become the
    // post-login redirect target - see sanitizeAuthTarget(). This matters
    // here specifically because this method reads window.location directly,
    // and can be reached while the browser is sitting on /logout (e.g. a
    // hard navigation there with an already-expired session).
    const targetPath = sanitizeAuthTarget(window.location.pathname + window.location.search);
    const fullTargetUrl = window.location.href;
    const callbackUrl = `${window.location.protocol}//${window.location.host}/auth/callback`;

    // Check for login_hint in URL to pass context to Auth0
    const params = new URLSearchParams(window.location.search);
    const loginHint = params.get('login_hint');

    const authorizationParams: any = {
      redirect_uri: callbackUrl,
      audience: this.configService.auth0.authorizationParams.audience,
      scope: 'openid profile email offline_access'
    };

    if (loginHint) {
      authorizationParams.login_hint = loginHint;
      // Also add target_subdomain for legacy support if needed
      if (loginHint.startsWith('subdomain:')) {
        authorizationParams.target_subdomain = loginHint.split(':')[1];
      }
    }

    // Set up app state
    const appState = {
      target: targetPath,
      fullUrl: fullTargetUrl,
      originalHost: window.location.host,
      originalProtocol: window.location.protocol
    };

    // Store fallback state in sessionStorage (matching AuthGuard logic)
    sessionStorage.setItem('auth_target_url', targetPath);
    sessionStorage.setItem('auth_full_url', fullTargetUrl);
    sessionStorage.setItem('auth_original_host', window.location.host);
    sessionStorage.setItem('auth_original_protocol', window.location.protocol);

    // Resolve the tenant SSO connection before redirecting. This closes the
    // path where a tenant subdomain redirect reached Auth0 without a connection
    // and reused a stale password session.
    const checkUrl = `${window.location.protocol}//${window.location.host}${targetPath}`;
    let selfHosted = false;
    try {
      const ssoInfo = await firstValueFrom(this.ssoResolver.lookup(checkUrl));
      this.storeSsoInfo(ssoInfo);
      if (ssoInfo.selfHosted) {
        selfHosted = true;
      } else if (ssoInfo.connection) {
        authorizationParams.connection = ssoInfo.connection;
        authorizationParams.target_subdomain = window.location.hostname.split('.')[0];
        console.log(`AuthInitializerService: forcing connection "${ssoInfo.connection}" for ${checkUrl}`);
      } else {
        console.warn(`AuthInitializerService: no SSO connection resolved for ${checkUrl}; redirect may use Universal Login`);
      }
    } catch (error) {
      console.warn('AuthInitializerService: SSO lookup failed, falling back to hostname derivation', error);
      const fallbackInfo = this.ssoResolver.fallbackFromUrl(checkUrl);
      this.storeSsoInfo(fallbackInfo);
      if (fallbackInfo.connection) {
        authorizationParams.connection = fallbackInfo.connection;
        authorizationParams.target_subdomain = window.location.hostname.split('.')[0];
      }
    }

    return { appState, authorizationParams, selfHosted };
  }

  public async login(): Promise<void> {
    // Mobile prompt response, open surveys, and other public routes must
    // never send the visitor to Auth0 login. A stale/absent session is
    // expected there; kicking off loginWithRedirect is #13276.
    if (isPublicAuthRoute()) {
      return;
    }

    // Salesforce Experience Cloud iframes block Auth0's redirect flow via CSP
    // frame-ancestors 'none'. Popup login is the workaround, but browsers
    // block window.open() without a user gesture — auto-calling loginWithPopup()
    // here produced SLAPFIVE-FRONTEND-A2X/A2Y/A32.
    //
    // Do not navigate or popup from here. AuthGuard.performLogin() sends
    // first-load iframe users to /auth/embed-login after sso-lookup (so the
    // SSO connection is stored). forceTokenRefresh() navigates there on
    // embed session expiry. Auto-navigating here also breaks Karma: the
    // test runner is itself an iframe, so any suite that constructs this
    // service would route to /auth/embed-login without that route configured.
    if (isRunningInIframe()) {
      return;
    }

    const config = await this.buildLoginConfig();

    // Self-hosted tenants (#16114) sign in on /auth/login, not Auth0.
    // auth_target_url was already stored by buildLoginConfig() above.
    if (config.selfHosted) {
      this.router.navigate(['/auth/login'], { replaceUrl: true });
      return;
    }

    this.setRedirectInProgress(true);
    this.auth.loginWithRedirect(config);
  }

  private storeSsoInfo(info: TenantSsoInfo): void {
    try {
      sessionStorage.setItem('sso_info', JSON.stringify(info));
    } catch {
      // Ignore storage errors
    }
  }

  async waitForAuth(): Promise<void> {
    if (this.isRedirectInProgress()) {
      throw new Error('REDIRECT_IN_PROGRESS');
    }

    const result = await firstValueFrom(
      race(
        this.token$.pipe(filter(t => t !== null), take(1)),
        this.redirectInProgress$.pipe(filter(r => r), take(1), map(() => 'REDIRECT' as const))
      ).pipe(timeout(5000))
    ).catch(() => null);

    if (result === 'REDIRECT') {
      throw new Error('REDIRECT_IN_PROGRESS');
    }

    if (result) {
      return;
    }

    // The cached token never became available within the wait window — this
    // can happen if something cleared it (e.g. an idle-timeout logout) or
    // initialization hasn't produced a value yet. Attempt one direct silent
    // refresh before giving up: if the underlying SSO session (e.g. the
    // Salesforce IdP backing an embed) is still valid, this recovers
    // immediately instead of failing every subsequent request forever.
    const refreshed = await this.forceTokenRefresh().catch(error => {
      // forceTokenRefresh() already records lastTokenRefreshError internally;
      // this defensive catch only guards against it throwing synchronously.
      this.lastTokenRefreshError = this.lastTokenRefreshError ?? error;
      return null;
    });
    if (refreshed) {
      return;
    }

    if (this.isRedirectInProgress()) {
      throw new Error('REDIRECT_IN_PROGRESS');
    }

    // #12587 / SLAPFIVE-FRONTEND-A2H: forceTokenRefresh() already navigated
    // (or left a public/embed-login page in place) for unrecoverable Auth0
    // errors. Throwing `No token available: login_required` here still
    // becomes an ApolloError via AuthWaitLink — the remaining production
    // noise after PR #12588. AuthWaitLink swallows REDIRECT_IN_PROGRESS.
    if (this.isUnrecoverableAuthError(this.lastTokenRefreshError)) {
      throw new Error('REDIRECT_IN_PROGRESS');
    }

    // Surface the underlying silent-refresh failure reason (e.g. timeout, a
    // blocked third-party cookie for the Auth0 iframe fallback, a network
    // error, ...) instead of a black-box message. See #11370.
    throw new Error(`No token available: ${this.describeTokenError(this.lastTokenRefreshError)}`);
  }

  private isUnrecoverableAuthError(error: unknown): boolean {
    if (!error) {
      return false;
    }
    const err = error as { error?: string; message?: string };
    return (
      err.error === 'login_required' ||
      err.error === 'consent_required' ||
      (!!err.message && err.message.includes('Missing Refresh Token'))
    );
  }

  /**
   * Best-effort human-readable description of a token-refresh failure, for
   * inclusion in diagnostic error messages (Sentry, logs). Never throws.
   */
  private describeTokenError(error: unknown): string {
    if (!error) {
      return 'unknown (no cached error)';
    }
    const err = error as { error?: string; message?: string };
    return err.error || err.message || String(error);
  }

  /**
   * Force a token refresh
   * This is useful when we encounter a Missing Refresh Token error, or when
   * waitForAuth() finds the cached token missing and needs to try recovering
   * it directly rather than just failing.
   */
  async forceTokenRefresh(): Promise<string | null> {
    // Concurrent callers (the proactive timer, getToken() on an expired token,
    // AuthWaitLink retries) share one refresh so they can't race each other's
    // setToken() calls or spend the refresh token twice.
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.refreshToken().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  private async refreshToken(): Promise<string | null> {
    try {
      // Self-hosted sessions (#16114): there is no Auth0 refresh path — the
      // stored SlapFive JWT is the session. When it expires, restoreSession()
      // re-mints from the shared-domain s5_session cookie; only when that
      // cookie is also gone does this fall through to the Auth0 flow, which
      // fails login_required and routes through AuthGuard to /auth/login —
      // correct behavior for a fully-expired self-hosted session.
      const selfHostedToken = await this.selfHostedAuth.restoreSession();
      if (selfHostedToken) {
        this.setToken(selfHostedToken);
        this.lastTokenRefreshError = null;
        return selfHostedToken;
      }

      // Keep the current token available while the replacement is fetched.
      // Clearing it here created a window where concurrent Apollo operations
      // were dispatched without Authorization and Hasura treated them as the
      // anonymous role (#14141).
      // Get a fresh token with full scope
      const token = await firstValueFrom(this.auth.getAccessTokenSilently({
        authorizationParams: {
          audience: this.configService.auth0.authorizationParams.audience,
          scope: 'openid profile email offline_access'
        },
        cacheMode: 'off' // Force a fresh token
      }));
      
      this.setToken(token);
      this.lastTokenRefreshError = null;
      return token;
    } catch (error: any) {
      console.error('Error forcing token refresh:', error);
      this.lastTokenRefreshError = error;

      // `login_required` (and its close cousin `consent_required`) is Auth0's
      // authoritative signal that silent re-authentication is impossible and
      // the user must interactively log back in — e.g. their session/SSO
      // cookie expired or was revoked. Unlike a timeout or network error,
      // this is not transient, so retrying won't help; treat it the same as
      // "Missing Refresh Token" below rather than letting it fall through to
      // a generic thrown Error that surfaces as an uncaught ApolloError (a
      // scary error modal + Sentry noise) for what is really just an
      // expired session. See SLAPFIVE-FRONTEND-A2H / #12587.
      if (this.isUnrecoverableAuthError(error)) {
        // Public routes (mobile prompt, open survey, story viewer, …) are
        // used by anonymous members. `login_required` is the expected
        // Auth0 signal there, not a reason to bounce them through
        // /client/home (AuthGuard) onto the S5 login page. See #13276.
        if (isPublicAuthRoute()) {
          console.log(`${error.error || 'Missing refresh token'} on a public route; leaving the visitor on the current page.`);
          return null;
        }

        // If we get a refresh token error, the session genuinely can't be
        // recovered silently. On embed routes (boards/companies/stories embedded
        // in Salesforce), redirect through the same SSO connection instead of
        // navigating to /client/home — if the Salesforce IdP session is still
        // alive this completes silently and lands back on the same embedded
        // page; only if Salesforce itself is also logged out will this surface
        // an interactive login, which is the correct behavior at that point.
        // Mark recovery as in progress before navigating so waitForAuth() does
        // not continue after this null refresh result and surface an expected
        // session expiry as an uncaught ApolloError.
        this.setRedirectInProgress(true);
        if (this.isEmbedRoute()) {
          console.log(`${error.error || 'Missing refresh token'} on an embed route, sending the user to click-to-sign-in.`);
          this.router.navigateByUrl('/auth/embed-login');
        } else {
          console.log(`${error.error || 'Missing refresh token'} during force refresh, navigating to home to re-trigger AuthGuard.`);
          this.router.navigate(['/client/home']);
        }
      }
      
      return null;
    }
  }

  /**
   * Cache the current token and (re)schedule a proactive silent refresh ahead
   * of its expiry, so long-lived tabs/embeds renew the token in the
   * background instead of only refreshing reactively after a request fails.
   */
  public setToken(token: string | null): void {
    this.tokenSubject.next(token);
    // A produced token means no login redirect is actually pending — clear a
    // stale flag so waitForAuth() doesn't hang callers on REDIRECT_IN_PROGRESS
    // forever (e.g. after forceTokenRefresh()'s Auth0 fallback set it while a
    // self-hosted session was still resumable via restoreSession()).
    if (token) {
      this.setRedirectInProgress(false);
    }
    this.scheduleProactiveRefresh(token);
  }

  /**
   * Marks auth as "ready" without independently fetching/setting a token.
   * Used by AuthCallbackComponent after it has set the fresh,
   * handleRedirectCallback()-derived token, so getToken()'s
   * `await firstValueFrom(authReady$...)` gates (which explicitly wait
   * whenever the pathname is /auth/callback, see #12221) unblock correctly.
   */
  public markAuthReady(): void {
    this.authReadySubject.next(true);
  }

  private scheduleProactiveRefresh(token: string | null): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (!token) {
      return;
    }

    const expiresAtMs = this.getTokenExpiryMs(token);
    if (!expiresAtMs) {
      // Opaque (non-JWT) access tokens can't be decoded for an exp claim;
      // fall back to reactive-only refresh for those.
      return;
    }

    const msUntilExpiry = expiresAtMs - Date.now();
    // Refresh at 80% of the remaining lifetime, with a sensible floor so a
    // near-expired token doesn't schedule an immediate refresh loop.
    const refreshInMs = Math.max(msUntilExpiry * 0.8, 10000);

    this.refreshTimer = setTimeout(() => {
      this.forceTokenRefresh().catch(error => {
        console.error('AuthInitializerService: proactive token refresh failed', error);
      });
    }, refreshInMs);
  }

  private isTokenExpiring(token: string): boolean {
    const expiresAtMs = this.getTokenExpiryMs(token);
    return expiresAtMs !== null && expiresAtMs - Date.now() < AuthInitializerService.TOKEN_EXPIRY_MARGIN_MS;
  }

  private getTokenExpiryMs(token: string): number | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return null;
      }
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(normalized));
      return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  /**
   * Whether the currently-activated route is one of the isEmbed:true routes
   * (boards/companies/stories embedded in an external CRM like Salesforce).
   * Mirrors ClientLayout.currentRouteIsEmbed() — kept in sync with the
   * `data: { isEmbed: true }` route config in client-admin.routes.ts.
   */
  private isEmbedRoute(): boolean {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return !!route.snapshot?.data?.['isEmbed'];
  }

  /**
   * Set the redirect in progress state
   * This is used to track when a redirect to Auth0 is in progress
   */
  setRedirectInProgress(inProgress: boolean): void {
    this.redirectInProgressSubject.next(inProgress);
    
    // If we're starting a redirect, store this in sessionStorage
    // This helps us detect redirects across page reloads
    if (inProgress) {
      sessionStorage.setItem('auth_redirect_in_progress', 'true');
    } else {
      sessionStorage.removeItem('auth_redirect_in_progress');
    }
  }
  
  /**
   * Check if a redirect is in progress
   */
  isRedirectInProgress(): boolean {
    // Check both the subject and sessionStorage
    // This handles the case where the page was reloaded during a redirect
    return this.redirectInProgressSubject.getValue() || 
           sessionStorage.getItem('auth_redirect_in_progress') === 'true';
  }
  
  /**
   * Check if the current route requires authentication
   */
  private isRouteRequiringAuth(): boolean {
    return !isPublicAuthRoute();
  }
  
  async getToken(): Promise<string | null> {
    // If we're on the callback route, wait for auth to complete before returning token.
    // This prevents GraphQL queries from proceeding without auth during callback->navigation.
    // See: https://github.com/slapfive/slapfive/issues/9259
    if (window.location.pathname === '/auth/callback') {
      await firstValueFrom(this.authReady$.pipe(filter(ready => ready), take(1)));
    }

    // If we're in the auth redirect flow, we can't get a token yet.
    if (this.isInAuthRedirect) {
      return null;
    }

    // Self-hosted sessions (#16114): a stored SlapFive JWT is authoritative —
    // it only exists after an explicit /auth/login or set-password flow, so
    // prefer it over any Auth0 SDK state. When the JWT has lapsed but the
    // shared-domain s5_session cookie is still live, restoreSession() mints a
    // fresh one instead of falling through to the Auth0 login_required path.
    // Phase 6 (#16117) replaces this precedence check with a provider-aware
    // driver split.
    const selfHostedToken = await this.selfHostedAuth.restoreSession();
    if (selfHostedToken) {
      if (this.tokenSubject.getValue() !== selfHostedToken) {
        this.setToken(selfHostedToken);
      }
      return selfHostedToken;
    }

    // Wait for the initializer to be completely finished.
    // This ensures we don't proceed until we know the auth state is stable.
    if (!this.initialized) {
      await firstValueFrom(this.authReady$.pipe(filter(ready => ready), take(1)));
    }

    // At this point, the initializer has run. We can trust the tokenSubject.
    const token = this.tokenSubject.getValue();

    // The proactive refresh is a setTimeout, which browsers throttle or freeze
    // in background tabs and across sleep, so a long-idle tab can still be
    // holding a token past its exp. Handing that to Hasura or api-server fails
    // every request (SLAPFIVE-FRONTEND-A63 / #16967); refresh it instead.
    if (token && !this.isTokenExpiring(token)) {
      return token;
    }

    // No usable cached token (expired, or cleared by an idle-timeout logout while an
    // embed's underlying SSO session is still perfectly valid). REST calls
    // don't go through AuthWaitLink's retry/refresh logic, so attempt one
    // direct silent refresh here rather than immediately giving up — except
    // on public routes, where a missing token is expected and a silent
    // refresh would surface login_required (#13276).
    if (isPublicAuthRoute()) {
      return null;
    }

    const refreshed = await this.forceTokenRefresh().catch(() => null);
    if (refreshed) {
      return refreshed;
    }

    // If there's still no token, the user is likely unauthenticated or the initial fetch failed.
    // The AuthGuard will handle redirecting them if the route is protected.
    return null;
  }
}
